import { Attributes, DateUtils, Misc, ModelBase, NeoModel, NumberUtils, Rules, Validation } from '@singularsystems/neo-core';
import Decimal from 'decimal.js-light';
import type { IInvestmentHost } from '../../Investment/IInvestmentHost';
import IssuerInvestment from '../../IssuerInvestments/IssuerInvestment';
import UtilisationLookup from '../../IssuerInvestments/Lookups/UtilisationLookup';
import ShareTransactionLookup from '../Lookups/ShareTransactionLookup';
import { BusinessDayConvention } from '../../Dividends/BusinessDayConvention.enum';

export enum TransactionType {
    Contribution = 1,
    Redemption = 2
}

@NeoModel
export default class ShareTransactionCommand extends ModelBase {

    constructor(investmentHost: IInvestmentHost) {
        super();

        const investment = investmentHost.investment,
            investmentLookups = investment.lookup;
        this.investmentId = investment.investmentId;

        this.investmentValues = {
            valuePerShare: investment.valuePerShare,
            balanceBefore: investmentLookups.shareTransactions.reduce((balanceBefore, shareTransaction) => balanceBefore.add(shareTransaction.amount), new Decimal(0)),
            prevShareTransaction: investmentLookups.shareTransactions.length === 0 ? null : investmentLookups.shareTransactions[investmentLookups.shareTransactions.length - 1],
            isIssuerInvestment: investmentHost instanceof IssuerInvestment,
            lastAllocationDate: investmentHost.commonFields.lastAllocationDate,
            isCeded: investmentHost.commonFields.isCeded,
            investorBalance: investmentLookups.investorBalance,
            minimumLimit: investmentLookups.minimumLimit,
            minimumTransactionAmount: investmentLookups.minimumTransactionAmount,
            paymentDateConvention: investment.calculationOptions.paymentDateConvention,
            paymentDateOffset: investment.calculationOptions.paymentDateOffset
        }
    }

    public investmentId: number = 0;

    @Rules.Required()
    @Attributes.Date(Misc.TimeZoneFormat.None)
    public transactionDate: Date | null = null;

    @Attributes.Integer()
    public noOfShares: number = 0;

    @Attributes.Date(Misc.TimeZoneFormat.None)
    public noticeDate: Date | null = null;

    // Client only properties / methods

    @Attributes.NoTracking()
    @Rules.Required()
    public transactionType: TransactionType | null = null;

    @Attributes.NoTracking()
    public investmentValues: {
        valuePerShare: number,
        balanceBefore: Decimal,
        prevShareTransaction: ShareTransactionLookup | null,
        isIssuerInvestment: boolean,
        lastAllocationDate: Date | null,
        isCeded: boolean,
        investorBalance: number,
        minimumLimit: number,
        minimumTransactionAmount: number,
        paymentDateConvention: BusinessDayConvention,
        paymentDateOffset: number
    }

    @Attributes.NoTracking()
    public noticePeriodDays: number | null = null;

    @Attributes.NoTracking()
    @Attributes.Date(Misc.TimeZoneFormat.None)
    public minDate: Date | null = null;

    @Attributes.NoTracking()
    @Attributes.Decimal()
    @Attributes.Display("Amount")
    public amountPositive: Decimal = new Decimal(0);

    @Attributes.Display("No of Shares")
    @Attributes.NoTracking()
    @Attributes.Decimal()
    public noOfSharesPositive: Decimal = new Decimal(0);

    @Attributes.NoTracking()
    public utilisation: UtilisationLookup | null = null;

    public get hasValidTransactionDate() {
        return this.transactionDate !== null && 
            (this.investmentValues.lastAllocationDate === null || this.transactionDate >= this.investmentValues.lastAllocationDate);
    }

    public onNoOfSharesChanged() {
        this.noOfShares = (this.transactionType === TransactionType.Contribution ? this.noOfSharesPositive : this.noOfSharesPositive.negated()).toNumber();
    }

    public calculateAmount() {
        this.amountPositive = this.noOfSharesPositive.mul(this.investmentValues.valuePerShare);
    }

    public calculateNoOfShares() {
        
        const noOfShares = this.amountPositive.dividedToIntegerBy(this.investmentValues.valuePerShare);
        this.noOfSharesPositive = noOfShares;
        this.onNoOfSharesChanged();
    }

    protected static addBusinessRules(rules: Validation.Rules<ShareTransactionCommand>) {
        super.addBusinessRules(rules);

        rules.failWhen(c => c.transactionType === TransactionType.Redemption && c.investmentValues.isCeded, "Cannot redeem from a ceded investment.");
        rules.failWhen(c => c.noOfSharesPositive.lt(0), "Please only enter positive values.").onProperties(c => c.noOfSharesPositive);
        rules.failWhen(c => c.noOfShares === 0, "No. of shares is required.");
        // if notice period is set, only allow negative?
        rules.failWhen(
            c => c.transactionDate !== null && !c.hasValidTransactionDate, 
            c => `Transaction date cannot be before ${DateUtils.format(c.investmentValues.lastAllocationDate!, "dd MMM yyyy")} because share allocations have been generated up to this date.`).onProperties(c => c.transactionDate);

        rules.failWhen(c => c.noticePeriodDays !== null && c.noticeDate === null, "Notice date is required.").onProperties(c => c.noticeDate);

        rules.add(ShareTransactionCommand.prototype.balanceRules).onProperties(c => c.amountPositive);
    }

    private balanceRules(context: Validation.IRuleContext) {
        const isRedemption = this.transactionType === TransactionType.Redemption;
        const amount = isRedemption ? this.amountPositive.negated() : this.amountPositive;
        const balanceAfter = amount.plus(this.investmentValues.balanceBefore);

        if (balanceAfter.lessThan(0)) {
            context.addError("Cannot redeem more than the investment balance.");
        } 
        if (this.utilisation) {
            if (this.investmentValues.isIssuerInvestment) {
                if (isRedemption && balanceAfter.lessThan(this.utilisation.utilisedAmount)) {
                    context.addError(`Cannot reduce balance below the utilised capacity of ${NumberUtils.format(this.utilisation.utilisedAmount, Misc.NumberFormat.CurrencyNoDecimals)}.`);
                }
            } else {
                const isLiquidityProvider = this.utilisation.liquidityUtilisation && this.utilisation.liquidityUtilisation.some(c => c.investmentId === this.investmentId);
                const spareCapacity = this.utilisation.getSpareCapacity(isLiquidityProvider);

                if (!isRedemption && amount.greaterThan(spareCapacity)) {
                    if (this.utilisation.hasLiquidityProviders && !isLiquidityProvider) {
                        context.addWarning(`Only ${NumberUtils.format(spareCapacity, Misc.NumberFormat.CurrencyNoDecimals)} is available for purchase. The remainder will be placed on the waitlist.`);
                    } else {
                        context.addError(`Current capacity is only ${NumberUtils.format(spareCapacity, Misc.NumberFormat.CurrencyNoDecimals)}.`);
                    }
                }

                if (isRedemption){

                    if (this.utilisation.hasLiquidityProviders && !isLiquidityProvider && amount.plus(this.utilisation.remainingLiquidity).lessThan(0)) {
                        context.addWarning(`Remaining liquidity is only ${NumberUtils.format(this.utilisation.remainingLiquidity, Misc.NumberFormat.CurrencyNoDecimals)}. Some / all of this transaction will be placed on the waitlist.`)
                    }

                    if (amount.plus(this.investmentValues.investorBalance).lessThan(this.investmentValues.minimumLimit)){
                        context.addError(`Cannot reduce the overall investors balance below ${NumberUtils.format(this.investmentValues.minimumLimit, Misc.NumberFormat.CurrencyNoDecimals)}`);
                    }
                }
            }
        }

        if (this.investmentValues.balanceBefore.equals(0) && balanceAfter.lessThan(this.investmentValues.minimumLimit)) {
            context.addError(`Minimum initial investment amount must be at least ${NumberUtils.format(this.investmentValues.minimumLimit, Misc.NumberFormat.CurrencyNoDecimals)}`);
        }

        if (this.amountPositive.lessThan(this.investmentValues.minimumTransactionAmount)) {
            context.addError(`Transaction amount must be >= ${NumberUtils.format(this.investmentValues.minimumTransactionAmount, Misc.NumberFormat.CurrencyNoDecimals)}`);
        }

        const amountCheck = this.noOfSharesPositive.mul(this.investmentValues.valuePerShare);
        if (!amountCheck.equals(this.amountPositive)) {
            context.addError("Amount captured is not a multiple of the value per share");
        }
    }

    public toString(): string {
        if (this.isNew) {
            return "New share transaction command";
        } else {
            return "Share Transaction Command";
        }
    }
}